// RigSentinel Calc — Subsea Rig Formulas, Batch 1: Riser Volumes & Capacities.
// Deterministic, pure-JS formulas. Imperial canonical units (in, ft, bbl, bbl/ft).
// The CalculatorRunner converts inputs/outputs to metric (mm, m, m³, L/m) via
// /app/frontend/src/lib/metricUnits.js when the user toggles MET.
//
// These five formulas DO NOT live under Well Control. They are added under the
// "subsea-rig-formulas" catalog category only.

import { round, num, positive } from "./_common";

// ---------- 1) Riser Capacity Per Foot ----------
//   C (bbl/ft) = ID² / 1029.4
export function riserCapacityPerFoot({ riserId }) {
  const ID = positive(num(riserId, "Riser ID"), "Riser ID");
  const v = (ID * ID) / 1029.4;
  return {
    result: round(v, 5),
    unit: "bbl/ft",
    formula: "C_riser (bbl/ft) = ID² / 1029.4",
    steps: [
      `Riser ID = ${ID} in`,
      `C = ${ID}² / 1029.4`,
      `C = ${round(v, 5)} bbl/ft`,
    ],
    inputs: { "Riser ID (in)": ID },
    extras: {
      "Capacity (gal/ft)": round(v * 42, 3),
    },
  };
}

// ---------- 2) Riser Total Internal Volume from Capacity ----------
//   V (bbl) = C (bbl/ft) × L (ft)
export function riserTotalInternalVolumeFromCapacity({ riserCapacityPerFt, riserLength }) {
  const C = positive(num(riserCapacityPerFt, "Riser Capacity"), "Riser Capacity");
  const L = positive(num(riserLength, "Riser Length"), "Riser Length");
  const v = C * L;
  return {
    result: round(v, 2),
    unit: "bbl",
    formula: "V_riser (bbl) = C (bbl/ft) × L (ft)",
    steps: [
      `Riser Capacity = ${C} bbl/ft,  Riser Length = ${L} ft`,
      `V = ${C} × ${L}`,
      `V = ${round(v, 2)} bbl`,
    ],
    inputs: { "Riser Capacity (bbl/ft)": C, "Riser Length (ft)": L },
    extras: {
      "Volume (gal)": round(v * 42, 1),
    },
  };
}

// ---------- 3) Riser Steel Displacement Per Foot ----------
//   D (bbl/ft) = (OD² − ID²) / 1029.4
export function riserSteelDisplacementPerFoot({ riserOd, riserId }) {
  const OD = positive(num(riserOd, "Riser OD"), "Riser OD");
  const ID = positive(num(riserId, "Riser ID"), "Riser ID");
  if (OD <= ID) {
    throw new Error("Riser OD must be greater than Riser ID");
  }
  const v = (OD * OD - ID * ID) / 1029.4;
  return {
    result: round(v, 5),
    unit: "bbl/ft",
    formula: "D_steel (bbl/ft) = (OD² − ID²) / 1029.4",
    steps: [
      `Riser OD = ${OD} in,  Riser ID = ${ID} in`,
      `D = (${OD}² − ${ID}²) / 1029.4`,
      `D = (${round(OD * OD, 3)} − ${round(ID * ID, 3)}) / 1029.4`,
      `D = ${round(v, 5)} bbl/ft`,
    ],
    inputs: { "Riser OD (in)": OD, "Riser ID (in)": ID },
    extras: {
      "Steel cross-section (in²)": round((Math.PI / 4) * (OD * OD - ID * ID), 3),
    },
  };
}

// ---------- 4) Riser × Drill Pipe Annular Capacity Per Foot ----------
//   Ca (bbl/ft) = (Riser ID² − DP OD²) / 1029.4
export function riserDpAnnularCapacityPerFoot({ riserId, dpOd }) {
  const ID = positive(num(riserId, "Riser ID"), "Riser ID");
  const OD = positive(num(dpOd, "Drill Pipe OD"), "Drill Pipe OD");
  if (ID <= OD) {
    throw new Error("Riser ID must be greater than Drill Pipe OD");
  }
  const v = (ID * ID - OD * OD) / 1029.4;
  return {
    result: round(v, 5),
    unit: "bbl/ft",
    formula: "Ca (bbl/ft) = (Riser ID² − DP OD²) / 1029.4",
    steps: [
      `Riser ID = ${ID} in,  Drill Pipe OD = ${OD} in`,
      `Ca = (${ID}² − ${OD}²) / 1029.4`,
      `Ca = (${round(ID * ID, 3)} − ${round(OD * OD, 3)}) / 1029.4`,
      `Ca = ${round(v, 5)} bbl/ft`,
    ],
    inputs: { "Riser ID (in)": ID, "Drill Pipe OD (in)": OD },
    extras: {
      "Annular cross-section (in²)": round((Math.PI / 4) * (ID * ID - OD * OD), 3),
    },
  };
}

// ---------- 5) Riser × Drill Pipe Annular Total Volume ----------
//   Va (bbl) = ((Riser ID² − DP OD²) × L) / 1029.4
export function riserDpAnnularVolumeTotal({ riserId, dpOd, riserLength }) {
  const ID = positive(num(riserId, "Riser ID"), "Riser ID");
  const OD = positive(num(dpOd, "Drill Pipe OD"), "Drill Pipe OD");
  const L = positive(num(riserLength, "Riser Length"), "Riser Length");
  if (ID <= OD) {
    throw new Error("Riser ID must be greater than Drill Pipe OD");
  }
  const capPerFt = (ID * ID - OD * OD) / 1029.4;
  const v = capPerFt * L;
  return {
    result: round(v, 2),
    unit: "bbl",
    formula: "Va (bbl) = ((Riser ID² − DP OD²) × L) / 1029.4",
    steps: [
      `Riser ID = ${ID} in,  Drill Pipe OD = ${OD} in,  Riser Length = ${L} ft`,
      `Capacity per ft = (${ID}² − ${OD}²) / 1029.4 = ${round(capPerFt, 5)} bbl/ft`,
      `Va = ${round(capPerFt, 5)} × ${L}`,
      `Va = ${round(v, 2)} bbl`,
    ],
    inputs: { "Riser ID (in)": ID, "Drill Pipe OD (in)": OD, "Riser Length (ft)": L },
    extras: {
      "Annular capacity (bbl/ft)": round(capPerFt, 5),
      "Volume (gal)": round(v * 42, 1),
    },
  };
}

// ============================================================================
// Subsea Rig Formulas — Batch 2: Choke & Kill Line Volumes, Strokes, and
// Circulation Time (10 calculators). Pure deterministic JS. Imperial canonical.
// ============================================================================

// ---------- 6) Choke Line Capacity Per Foot ----------
//   C (bbl/ft) = ID² / 1029.4
export function chokeLineCapacityPerFoot({ chokeLineId }) {
  const ID = positive(num(chokeLineId, "Choke Line ID"), "Choke Line ID");
  const v = (ID * ID) / 1029.4;
  return {
    result: round(v, 5),
    unit: "bbl/ft",
    formula: "C_choke (bbl/ft) = ID² / 1029.4",
    steps: [
      `Choke Line ID = ${ID} in`,
      `C = ${ID}² / 1029.4`,
      `C = ${round(v, 5)} bbl/ft`,
    ],
    inputs: { "Choke Line ID (in)": ID },
    extras: { "Capacity (gal/ft)": round(v * 42, 4) },
  };
}

// ---------- 7) Choke Line Total Volume ----------
//   V (bbl) = ID² × L / 1029.4
export function chokeLineTotalVolume({ chokeLineId, chokeLineLength }) {
  const ID = positive(num(chokeLineId, "Choke Line ID"), "Choke Line ID");
  const L  = positive(num(chokeLineLength, "Choke Line Length"), "Choke Line Length");
  const v = (ID * ID * L) / 1029.4;
  return {
    result: round(v, 2),
    unit: "bbl",
    formula: "V_choke (bbl) = ID² × L / 1029.4",
    steps: [
      `Choke Line ID = ${ID} in,  Length = ${L} ft`,
      `V = ${ID}² × ${L} / 1029.4`,
      `V = ${round(v, 2)} bbl`,
    ],
    inputs: { "Choke Line ID (in)": ID, "Choke Line Length (ft)": L },
    extras: { "Volume (gal)": round(v * 42, 1) },
  };
}

// ---------- 8) Kill Line Capacity Per Foot ----------
//   C (bbl/ft) = ID² / 1029.4
export function killLineCapacityPerFoot({ killLineId }) {
  const ID = positive(num(killLineId, "Kill Line ID"), "Kill Line ID");
  const v = (ID * ID) / 1029.4;
  return {
    result: round(v, 5),
    unit: "bbl/ft",
    formula: "C_kill (bbl/ft) = ID² / 1029.4",
    steps: [
      `Kill Line ID = ${ID} in`,
      `C = ${ID}² / 1029.4`,
      `C = ${round(v, 5)} bbl/ft`,
    ],
    inputs: { "Kill Line ID (in)": ID },
    extras: { "Capacity (gal/ft)": round(v * 42, 4) },
  };
}

// ---------- 9) Kill Line Total Volume ----------
//   V (bbl) = ID² × L / 1029.4
export function killLineTotalVolume({ killLineId, killLineLength }) {
  const ID = positive(num(killLineId, "Kill Line ID"), "Kill Line ID");
  const L  = positive(num(killLineLength, "Kill Line Length"), "Kill Line Length");
  const v = (ID * ID * L) / 1029.4;
  return {
    result: round(v, 2),
    unit: "bbl",
    formula: "V_kill (bbl) = ID² × L / 1029.4",
    steps: [
      `Kill Line ID = ${ID} in,  Length = ${L} ft`,
      `V = ${ID}² × ${L} / 1029.4`,
      `V = ${round(v, 2)} bbl`,
    ],
    inputs: { "Kill Line ID (in)": ID, "Kill Line Length (ft)": L },
    extras: { "Volume (gal)": round(v * 42, 1) },
  };
}

// ---------- 10) Total Choke + Kill Line Volume ----------
//   V_total (bbl) = V_choke (bbl) + V_kill (bbl)
export function totalChokeKillLineVolume({ chokeLineVolume, killLineVolume }) {
  const Vc = positive(num(chokeLineVolume, "Choke Line Volume"), "Choke Line Volume");
  const Vk = positive(num(killLineVolume, "Kill Line Volume"), "Kill Line Volume");
  const v = Vc + Vk;
  return {
    result: round(v, 2),
    unit: "bbl",
    formula: "V_total (bbl) = V_choke + V_kill",
    steps: [
      `V_choke = ${Vc} bbl,  V_kill = ${Vk} bbl`,
      `V_total = ${Vc} + ${Vk}`,
      `V_total = ${round(v, 2)} bbl`,
    ],
    inputs: { "Choke Line Volume (bbl)": Vc, "Kill Line Volume (bbl)": Vk },
    extras: { "Volume (gal)": round(v * 42, 1) },
  };
}

// ---------- 11) Choke Line Strokes ----------
//   Strokes = V_choke (bbl) / pump_output (bbl/stk)
export function chokeLineStrokes({ chokeLineVolume, pumpOutput }) {
  const V  = positive(num(chokeLineVolume, "Choke Line Volume"), "Choke Line Volume");
  const PO = positive(num(pumpOutput, "Pump Output"), "Pump Output");
  const n = V / PO;
  return {
    result: round(n, 2),
    unit: "strokes",
    formula: "Strokes = V_choke / pump_output",
    steps: [
      `V_choke = ${V} bbl,  Pump Output = ${PO} bbl/stk`,
      `Strokes = ${V} / ${PO}`,
      `Strokes = ${round(n, 2)}`,
    ],
    inputs: { "Choke Line Volume (bbl)": V, "Pump Output (bbl/stk)": PO },
  };
}

// ---------- 12) Kill Line Strokes ----------
//   Strokes = V_kill (bbl) / pump_output (bbl/stk)
export function killLineStrokes({ killLineVolume, pumpOutput }) {
  const V  = positive(num(killLineVolume, "Kill Line Volume"), "Kill Line Volume");
  const PO = positive(num(pumpOutput, "Pump Output"), "Pump Output");
  const n = V / PO;
  return {
    result: round(n, 2),
    unit: "strokes",
    formula: "Strokes = V_kill / pump_output",
    steps: [
      `V_kill = ${V} bbl,  Pump Output = ${PO} bbl/stk`,
      `Strokes = ${V} / ${PO}`,
      `Strokes = ${round(n, 2)}`,
    ],
    inputs: { "Kill Line Volume (bbl)": V, "Pump Output (bbl/stk)": PO },
  };
}

// ---------- 13) Choke Line Circulation Time ----------
//   t (min) = V_choke (bbl) / pump_rate (bbl/min)
export function chokeLineCirculationTime({ chokeLineVolume, pumpRate }) {
  const V  = positive(num(chokeLineVolume, "Choke Line Volume"), "Choke Line Volume");
  const Q  = positive(num(pumpRate, "Pump Rate"), "Pump Rate");
  const t = V / Q;
  return {
    result: round(t, 2),
    unit: "min",
    formula: "t (min) = V_choke / Q",
    steps: [
      `V_choke = ${V} bbl,  Q = ${Q} bbl/min`,
      `t = ${V} / ${Q}`,
      `t = ${round(t, 2)} min`,
    ],
    inputs: { "Choke Line Volume (bbl)": V, "Pump Rate (bbl/min)": Q },
    extras: { "Time (hr)": round(t / 60, 3) },
  };
}

// ---------- 14) Kill Line Circulation Time ----------
//   t (min) = V_kill (bbl) / pump_rate (bbl/min)
export function killLineCirculationTime({ killLineVolume, pumpRate }) {
  const V  = positive(num(killLineVolume, "Kill Line Volume"), "Kill Line Volume");
  const Q  = positive(num(pumpRate, "Pump Rate"), "Pump Rate");
  const t = V / Q;
  return {
    result: round(t, 2),
    unit: "min",
    formula: "t (min) = V_kill / Q",
    steps: [
      `V_kill = ${V} bbl,  Q = ${Q} bbl/min`,
      `t = ${V} / ${Q}`,
      `t = ${round(t, 2)} min`,
    ],
    inputs: { "Kill Line Volume (bbl)": V, "Pump Rate (bbl/min)": Q },
    extras: { "Time (hr)": round(t / 60, 3) },
  };
}

// ---------- 15) Combined Choke + Kill Line Strokes ----------
//   Strokes = V_total (bbl) / pump_output (bbl/stk)
export function combinedChokeKillLineStrokes({ totalLineVolume, pumpOutput }) {
  const V  = positive(num(totalLineVolume, "Total C/K Line Volume"), "Total C/K Line Volume");
  const PO = positive(num(pumpOutput, "Pump Output"), "Pump Output");
  const n = V / PO;
  return {
    result: round(n, 2),
    unit: "strokes",
    formula: "Strokes = V_total / pump_output",
    steps: [
      `V_total = ${V} bbl,  Pump Output = ${PO} bbl/stk`,
      `Strokes = ${V} / ${PO}`,
      `Strokes = ${round(n, 2)}`,
    ],
    inputs: { "Total C/K Line Volume (bbl)": V, "Pump Output (bbl/stk)": PO },
  };
}

// ============================================================================
// Subsea Rig Formulas — Batch 3: Depth References + Riser Hydrostatics +
// Riser Margin (15 calculators). Pure deterministic JS. Imperial canonical.
// ============================================================================

// ---------- 16) RKB to Mudline Depth ----------
//   D_mudline = RKB_to_SeaLevel + WaterDepth
export function rkbToMudlineDepth({ rkbToSeaLevel, waterDepth }) {
  const RKB = positive(num(rkbToSeaLevel, "RKB to Sea Level"), "RKB to Sea Level");
  const WD  = positive(num(waterDepth, "Water Depth"), "Water Depth");
  const v = RKB + WD;
  return {
    result: round(v, 2),
    unit: "ft",
    formula: "D_mudline = RKB_to_SeaLevel + WaterDepth",
    steps: [
      `RKB to Sea Level = ${RKB} ft,  Water Depth = ${WD} ft`,
      `D_mudline = ${RKB} + ${WD}`,
      `D_mudline = ${round(v, 2)} ft`,
    ],
    inputs: { "RKB to Sea Level (ft)": RKB, "Water Depth (ft)": WD },
  };
}

// ---------- 17) Water Depth from RKB and Mudline ----------
//   WaterDepth = RKB_to_Mudline − RKB_to_SeaLevel
export function waterDepthFromRkbAndMudline({ rkbToMudline, rkbToSeaLevel }) {
  const M   = positive(num(rkbToMudline, "RKB to Mudline"), "RKB to Mudline");
  const RKB = positive(num(rkbToSeaLevel, "RKB to Sea Level"), "RKB to Sea Level");
  if (M <= RKB) {
    throw new Error("RKB to Mudline must be greater than RKB to Sea Level");
  }
  const v = M - RKB;
  return {
    result: round(v, 2),
    unit: "ft",
    formula: "WaterDepth = RKB_to_Mudline − RKB_to_SeaLevel",
    steps: [
      `RKB to Mudline = ${M} ft,  RKB to Sea Level = ${RKB} ft`,
      `WaterDepth = ${M} − ${RKB}`,
      `WaterDepth = ${round(v, 2)} ft`,
    ],
    inputs: { "RKB to Mudline (ft)": M, "RKB to Sea Level (ft)": RKB },
  };
}

// ---------- 18) Subsea Wellhead Depth from RKB ----------
//   D_wh = RKB_to_SeaLevel + WaterDepth
export function subseaWellheadDepthFromRkb({ rkbToSeaLevel, waterDepth }) {
  const RKB = positive(num(rkbToSeaLevel, "RKB to Sea Level"), "RKB to Sea Level");
  const WD  = positive(num(waterDepth, "Water Depth"), "Water Depth");
  const v = RKB + WD;
  return {
    result: round(v, 2),
    unit: "ft",
    formula: "D_wh = RKB_to_SeaLevel + WaterDepth",
    steps: [
      `RKB to Sea Level = ${RKB} ft,  Water Depth = ${WD} ft`,
      `D_wh = ${RKB} + ${WD}`,
      `D_wh = ${round(v, 2)} ft`,
    ],
    inputs: { "RKB to Sea Level (ft)": RKB, "Water Depth (ft)": WD },
  };
}

// ---------- 19) Stack Depth from RKB ----------
//   D_stack = RKB_to_SeaLevel + WaterDepth + StackHeightAllowance
export function stackDepthFromRkb({ rkbToSeaLevel, waterDepth, stackHeightAllowance }) {
  const RKB = positive(num(rkbToSeaLevel, "RKB to Sea Level"), "RKB to Sea Level");
  const WD  = positive(num(waterDepth, "Water Depth"), "Water Depth");
  const SH  = positive(num(stackHeightAllowance, "Stack Height Allowance"), "Stack Height Allowance");
  const v = RKB + WD + SH;
  return {
    result: round(v, 2),
    unit: "ft",
    formula: "D_stack = RKB_to_SeaLevel + WaterDepth + StackHeightAllowance",
    steps: [
      `RKB to Sea Level = ${RKB} ft,  Water Depth = ${WD} ft,  Stack Height = ${SH} ft`,
      `D_stack = ${RKB} + ${WD} + ${SH}`,
      `D_stack = ${round(v, 2)} ft`,
    ],
    inputs: {
      "RKB to Sea Level (ft)": RKB,
      "Water Depth (ft)": WD,
      "Stack Height Allowance (ft)": SH,
    },
  };
}

// ---------- 20) Stack Height Allowance ----------
//   SH = StackDepth − RKB_to_Mudline
export function stackHeightAllowance({ stackDepthFromRkb, rkbToMudline }) {
  const S = positive(num(stackDepthFromRkb, "Stack Depth from RKB"), "Stack Depth from RKB");
  const M = positive(num(rkbToMudline, "RKB to Mudline"), "RKB to Mudline");
  const v = S - M;
  return {
    result: round(v, 2),
    unit: "ft",
    formula: "SH = StackDepth − RKB_to_Mudline",
    steps: [
      `Stack Depth = ${S} ft,  RKB to Mudline = ${M} ft`,
      `SH = ${S} − ${M}`,
      `SH = ${round(v, 2)} ft`,
    ],
    inputs: { "Stack Depth from RKB (ft)": S, "RKB to Mudline (ft)": M },
  };
}

// ---------- 21) RKB to Sea Level from Mudline Reference ----------
//   RKB_to_SeaLevel = RKB_to_Mudline − WaterDepth
export function rkbToSeaLevelFromMudline({ rkbToMudline, waterDepth }) {
  const M  = positive(num(rkbToMudline, "RKB to Mudline"), "RKB to Mudline");
  const WD = positive(num(waterDepth, "Water Depth"), "Water Depth");
  if (M <= WD) {
    throw new Error("RKB to Mudline must be greater than Water Depth");
  }
  const v = M - WD;
  return {
    result: round(v, 2),
    unit: "ft",
    formula: "RKB_to_SeaLevel = RKB_to_Mudline − WaterDepth",
    steps: [
      `RKB to Mudline = ${M} ft,  Water Depth = ${WD} ft`,
      `RKB to Sea Level = ${M} − ${WD}`,
      `RKB to Sea Level = ${round(v, 2)} ft`,
    ],
    inputs: { "RKB to Mudline (ft)": M, "Water Depth (ft)": WD },
  };
}

// ---------- 22) Riser Mud Hydrostatic Pressure ----------
//   P = 0.052 × MW × TVD
export function riserMudHydrostaticPressure({ mw, riserTvd }) {
  const MW  = positive(num(mw, "Mud Weight"), "Mud Weight");
  const TVD = positive(num(riserTvd, "Riser TVD"), "Riser TVD");
  const v = 0.052 * MW * TVD;
  return {
    result: round(v, 2),
    unit: "psi",
    formula: "P = 0.052 × MW × TVD",
    steps: [
      `MW = ${MW} ppg,  Riser TVD = ${TVD} ft`,
      `P = 0.052 × ${MW} × ${TVD}`,
      `P = ${round(v, 2)} psi`,
    ],
    inputs: { "Mud Weight (ppg)": MW, "Riser TVD (ft)": TVD },
  };
}

// ---------- 23) Seawater Hydrostatic Pressure (rig-side) ----------
//   P = Gradient × WaterDepth (Gradient default 0.445 psi/ft)
export function seawaterHydrostaticPressureRig({ waterDepth, seawaterGradient }) {
  const WD = positive(num(waterDepth, "Water Depth"), "Water Depth");
  const G  = seawaterGradient === "" || seawaterGradient === null || seawaterGradient === undefined
    ? 0.445
    : positive(num(seawaterGradient, "Seawater Gradient"), "Seawater Gradient");
  const v = G * WD;
  return {
    result: round(v, 2),
    unit: "psi",
    formula: "P = Gradient × WaterDepth",
    steps: [
      `Water Depth = ${WD} ft,  Gradient = ${G} psi/ft`,
      `P = ${G} × ${WD}`,
      `P = ${round(v, 2)} psi`,
    ],
    inputs: { "Water Depth (ft)": WD, "Seawater Gradient (psi/ft)": G },
  };
}

// ---------- 24) Riser Differential Hydrostatic Pressure ----------
//   ΔP = P_mud − P_sw
export function riserDifferentialHydrostaticPressure({ mudHydrostatic, seawaterHydrostatic }) {
  const PM = positive(num(mudHydrostatic, "Riser Mud Hydrostatic Pressure"), "Riser Mud Hydrostatic Pressure");
  const PS = positive(num(seawaterHydrostatic, "Seawater Hydrostatic Pressure"), "Seawater Hydrostatic Pressure");
  const v = PM - PS;
  return {
    result: round(v, 2),
    unit: "psi",
    formula: "ΔP = P_mud − P_seawater",
    steps: [
      `P_mud = ${PM} psi,  P_seawater = ${PS} psi`,
      `ΔP = ${PM} − ${PS}`,
      `ΔP = ${round(v, 2)} psi`,
    ],
    inputs: {
      "Riser Mud Hydrostatic Pressure (psi)": PM,
      "Seawater Hydrostatic Pressure (psi)": PS,
    },
  };
}

// ---------- 25) Riser Margin Pressure (Subsea Rig Formulas variant) ----------
//   P = 0.052 × RiserMargin × WaterDepth
export function riserMarginPressureSrf({ riserMarginPpg, waterDepth }) {
  const M  = positive(num(riserMarginPpg, "Riser Margin"), "Riser Margin");
  const WD = positive(num(waterDepth, "Water Depth"), "Water Depth");
  const v = 0.052 * M * WD;
  return {
    result: round(v, 2),
    unit: "psi",
    formula: "P = 0.052 × RiserMargin × WaterDepth",
    steps: [
      `Riser Margin = ${M} ppg,  Water Depth = ${WD} ft`,
      `P = 0.052 × ${M} × ${WD}`,
      `P = ${round(v, 2)} psi`,
    ],
    inputs: { "Riser Margin (ppg)": M, "Water Depth (ft)": WD },
  };
}

// ---------- 26) Required Riser Margin Mud Weight ----------
//   M = ΔP / (0.052 × WD)
export function requiredRiserMarginMudWeight({ desiredPressure, waterDepth }) {
  const DP = positive(num(desiredPressure, "Desired Pressure Margin"), "Desired Pressure Margin");
  const WD = positive(num(waterDepth, "Water Depth"), "Water Depth");
  const v = DP / (0.052 * WD);
  return {
    result: round(v, 3),
    unit: "ppg",
    formula: "M = ΔP / (0.052 × WaterDepth)",
    steps: [
      `ΔP = ${DP} psi,  Water Depth = ${WD} ft`,
      `M = ${DP} / (0.052 × ${WD})`,
      `M = ${round(v, 3)} ppg`,
    ],
    inputs: { "Desired Pressure Margin (psi)": DP, "Water Depth (ft)": WD },
  };
}

// ---------- 27) Mud Weight Equivalent of Seawater Gradient ----------
//   EMW = Gradient / 0.052
export function mudWeightEquivalentOfSeawaterGradient({ seawaterGradient }) {
  const G = positive(num(seawaterGradient, "Seawater Gradient"), "Seawater Gradient");
  const v = G / 0.052;
  return {
    result: round(v, 2),
    unit: "ppg",
    formula: "EMW = Gradient / 0.052",
    steps: [
      `Seawater Gradient = ${G} psi/ft`,
      `EMW = ${G} / 0.052`,
      `EMW = ${round(v, 2)} ppg`,
    ],
    inputs: { "Seawater Gradient (psi/ft)": G },
  };
}

// ---------- 28) Mud vs Seawater Gradient Difference ----------
//   ΔG = (0.052 × MW) − Gradient_sw
export function mudVsSeawaterGradientDifference({ mw, seawaterGradient }) {
  const MW = positive(num(mw, "Mud Weight"), "Mud Weight");
  const GS = positive(num(seawaterGradient, "Seawater Gradient"), "Seawater Gradient");
  const mudGrad = 0.052 * MW;
  const v = mudGrad - GS;
  return {
    result: round(v, 4),
    unit: "psi/ft",
    formula: "ΔG = (0.052 × MW) − Gradient_seawater",
    steps: [
      `MW = ${MW} ppg,  Seawater Gradient = ${GS} psi/ft`,
      `Mud Gradient = 0.052 × ${MW} = ${round(mudGrad, 4)} psi/ft`,
      `ΔG = ${round(mudGrad, 4)} − ${GS}`,
      `ΔG = ${round(v, 4)} psi/ft`,
    ],
    inputs: { "Mud Weight (ppg)": MW, "Seawater Gradient (psi/ft)": GS },
    extras: { "Mud Gradient (psi/ft)": round(mudGrad, 4) },
  };
}

// ---------- 29) Riser Differential from Gradient Difference ----------
//   ΔP = ΔG × WaterDepth
export function riserDifferentialFromGradientDifference({ gradientDifference, waterDepth }) {
  const G  = positive(num(gradientDifference, "Gradient Difference"), "Gradient Difference");
  const WD = positive(num(waterDepth, "Water Depth"), "Water Depth");
  const v = G * WD;
  return {
    result: round(v, 2),
    unit: "psi",
    formula: "ΔP = ΔG × WaterDepth",
    steps: [
      `ΔG = ${G} psi/ft,  Water Depth = ${WD} ft`,
      `ΔP = ${G} × ${WD}`,
      `ΔP = ${round(v, 2)} psi`,
    ],
    inputs: { "Gradient Difference (psi/ft)": G, "Water Depth (ft)": WD },
  };
}

// ---------- 30) Seawater Gradient from Pressure and Depth ----------
//   Gradient = P / WaterDepth
export function seawaterGradientFromPressureAndDepth({ seawaterHydrostatic, waterDepth }) {
  const P  = positive(num(seawaterHydrostatic, "Seawater Hydrostatic Pressure"), "Seawater Hydrostatic Pressure");
  const WD = positive(num(waterDepth, "Water Depth"), "Water Depth");
  const v = P / WD;
  return {
    result: round(v, 4),
    unit: "psi/ft",
    formula: "Gradient = P / WaterDepth",
    steps: [
      `P = ${P} psi,  Water Depth = ${WD} ft`,
      `Gradient = ${P} / ${WD}`,
      `Gradient = ${round(v, 4)} psi/ft`,
    ],
    inputs: { "Seawater Hydrostatic Pressure (psi)": P, "Water Depth (ft)": WD },
  };
}

// ============================================================================
// Subsea Rig Formulas — Final Beta Batch: Riser / Line Strokes, Circulation
// Time, Boost Line, and Auxiliary Line Support (15 calculators).
// Pure deterministic JS. Imperial canonical.
// All IDs use the `_srf` suffix to guarantee no collision with existing calcs.
// ============================================================================

// ---------- 31) Marine Riser Strokes ----------
//   Strokes = V_riser / pump_output
export function marineRiserStrokesSrf({ riserVolume, pumpOutput }) {
  const V  = positive(num(riserVolume, "Riser Volume"), "Riser Volume");
  const PO = positive(num(pumpOutput, "Pump Output"), "Pump Output");
  const n = V / PO;
  return {
    result: round(n, 2),
    unit: "strokes",
    formula: "Strokes = V_riser / pump_output",
    steps: [
      `V_riser = ${V} bbl,  Pump Output = ${PO} bbl/stk`,
      `Strokes = ${V} / ${PO}`,
      `Strokes = ${round(n, 2)}`,
    ],
    inputs: { "Riser Volume (bbl)": V, "Pump Output (bbl/stk)": PO },
  };
}

// ---------- 32) Marine Riser Circulation Time ----------
//   t (min) = V_riser / Q
export function marineRiserCirculationTimeSrf({ riserVolume, pumpRate }) {
  const V = positive(num(riserVolume, "Riser Volume"), "Riser Volume");
  const Q = positive(num(pumpRate, "Pump Rate"), "Pump Rate");
  const t = V / Q;
  return {
    result: round(t, 2),
    unit: "min",
    formula: "t (min) = V_riser / Q",
    steps: [
      `V_riser = ${V} bbl,  Q = ${Q} bbl/min`,
      `t = ${V} / ${Q}`,
      `t = ${round(t, 2)} min`,
    ],
    inputs: { "Riser Volume (bbl)": V, "Pump Rate (bbl/min)": Q },
    extras: { "Time (hr)": round(t / 60, 3) },
  };
}

// ---------- 33) Riser Annular Strokes ----------
//   Strokes = V_annular / pump_output
export function riserAnnularStrokesSrf({ riserAnnularVolume, pumpOutput }) {
  const V  = positive(num(riserAnnularVolume, "Riser Annular Volume"), "Riser Annular Volume");
  const PO = positive(num(pumpOutput, "Pump Output"), "Pump Output");
  const n = V / PO;
  return {
    result: round(n, 2),
    unit: "strokes",
    formula: "Strokes = V_annular / pump_output",
    steps: [
      `V_annular = ${V} bbl,  Pump Output = ${PO} bbl/stk`,
      `Strokes = ${V} / ${PO}`,
      `Strokes = ${round(n, 2)}`,
    ],
    inputs: { "Riser Annular Volume (bbl)": V, "Pump Output (bbl/stk)": PO },
  };
}

// ---------- 34) Riser Annular Circulation Time ----------
//   t (min) = V_annular / Q
export function riserAnnularCirculationTimeSrf({ riserAnnularVolume, pumpRate }) {
  const V = positive(num(riserAnnularVolume, "Riser Annular Volume"), "Riser Annular Volume");
  const Q = positive(num(pumpRate, "Pump Rate"), "Pump Rate");
  const t = V / Q;
  return {
    result: round(t, 2),
    unit: "min",
    formula: "t (min) = V_annular / Q",
    steps: [
      `V_annular = ${V} bbl,  Q = ${Q} bbl/min`,
      `t = ${V} / ${Q}`,
      `t = ${round(t, 2)} min`,
    ],
    inputs: { "Riser Annular Volume (bbl)": V, "Pump Rate (bbl/min)": Q },
    extras: { "Time (hr)": round(t / 60, 3) },
  };
}

// ---------- 35) Choke Line Pump Rate from Time ----------
//   Q (bbl/min) = V_choke / t
export function chokeLinePumpRateFromTimeSrf({ chokeLineVolume, time }) {
  const V = positive(num(chokeLineVolume, "Choke Line Volume"), "Choke Line Volume");
  const T = positive(num(time, "Time"), "Time");
  const q = V / T;
  return {
    result: round(q, 2),
    unit: "bbl/min",
    formula: "Q = V_choke / t",
    steps: [
      `V_choke = ${V} bbl,  t = ${T} min`,
      `Q = ${V} / ${T}`,
      `Q = ${round(q, 2)} bbl/min`,
    ],
    inputs: { "Choke Line Volume (bbl)": V, "Time (min)": T },
  };
}

// ---------- 36) Kill Line Pump Rate from Time ----------
//   Q (bbl/min) = V_kill / t
export function killLinePumpRateFromTimeSrf({ killLineVolume, time }) {
  const V = positive(num(killLineVolume, "Kill Line Volume"), "Kill Line Volume");
  const T = positive(num(time, "Time"), "Time");
  const q = V / T;
  return {
    result: round(q, 2),
    unit: "bbl/min",
    formula: "Q = V_kill / t",
    steps: [
      `V_kill = ${V} bbl,  t = ${T} min`,
      `Q = ${V} / ${T}`,
      `Q = ${round(q, 2)} bbl/min`,
    ],
    inputs: { "Kill Line Volume (bbl)": V, "Time (min)": T },
  };
}

// ---------- 37) Combined Choke + Kill Line Circulation Time ----------
//   t = V_total / Q
export function combinedChokeKillLineCirculationTimeSrf({ totalLineVolume, pumpRate }) {
  const V = positive(num(totalLineVolume, "Total C/K Line Volume"), "Total C/K Line Volume");
  const Q = positive(num(pumpRate, "Pump Rate"), "Pump Rate");
  const t = V / Q;
  return {
    result: round(t, 2),
    unit: "min",
    formula: "t = V_total / Q",
    steps: [
      `V_total = ${V} bbl,  Q = ${Q} bbl/min`,
      `t = ${V} / ${Q}`,
      `t = ${round(t, 2)} min`,
    ],
    inputs: { "Total C/K Line Volume (bbl)": V, "Pump Rate (bbl/min)": Q },
    extras: { "Time (hr)": round(t / 60, 3) },
  };
}

// ---------- 38) Combined Choke + Kill Line Pump Rate from Time ----------
//   Q = V_total / t
export function combinedChokeKillLinePumpRateFromTimeSrf({ totalLineVolume, time }) {
  const V = positive(num(totalLineVolume, "Total C/K Line Volume"), "Total C/K Line Volume");
  const T = positive(num(time, "Time"), "Time");
  const q = V / T;
  return {
    result: round(q, 2),
    unit: "bbl/min",
    formula: "Q = V_total / t",
    steps: [
      `V_total = ${V} bbl,  t = ${T} min`,
      `Q = ${V} / ${T}`,
      `Q = ${round(q, 2)} bbl/min`,
    ],
    inputs: { "Total C/K Line Volume (bbl)": V, "Time (min)": T },
  };
}

// ---------- 39) Boost Line Capacity Per Foot ----------
//   C (bbl/ft) = ID² / 1029.4
export function boostLineCapacityPerFootSrf({ boostLineId }) {
  const ID = positive(num(boostLineId, "Boost Line ID"), "Boost Line ID");
  const v = (ID * ID) / 1029.4;
  return {
    result: round(v, 5),
    unit: "bbl/ft",
    formula: "C_boost (bbl/ft) = ID² / 1029.4",
    steps: [
      `Boost Line ID = ${ID} in`,
      `C = ${ID}² / 1029.4`,
      `C = ${round(v, 5)} bbl/ft`,
    ],
    inputs: { "Boost Line ID (in)": ID },
    extras: { "Capacity (gal/ft)": round(v * 42, 4) },
  };
}

// ---------- 40) Boost Line Total Volume ----------
//   V (bbl) = ID² × L / 1029.4
export function boostLineTotalVolumeSrf({ boostLineId, boostLineLength }) {
  const ID = positive(num(boostLineId, "Boost Line ID"), "Boost Line ID");
  const L  = positive(num(boostLineLength, "Boost Line Length"), "Boost Line Length");
  const v = (ID * ID * L) / 1029.4;
  return {
    result: round(v, 2),
    unit: "bbl",
    formula: "V_boost (bbl) = ID² × L / 1029.4",
    steps: [
      `Boost Line ID = ${ID} in,  Length = ${L} ft`,
      `V = ${ID}² × ${L} / 1029.4`,
      `V = ${round(v, 2)} bbl`,
    ],
    inputs: { "Boost Line ID (in)": ID, "Boost Line Length (ft)": L },
    extras: { "Volume (gal)": round(v * 42, 1) },
  };
}

// ---------- 41) Boost Line Strokes ----------
//   Strokes = V_boost / pump_output
export function boostLineStrokesSrf({ boostLineVolume, pumpOutput }) {
  const V  = positive(num(boostLineVolume, "Boost Line Volume"), "Boost Line Volume");
  const PO = positive(num(pumpOutput, "Pump Output"), "Pump Output");
  const n = V / PO;
  return {
    result: round(n, 2),
    unit: "strokes",
    formula: "Strokes = V_boost / pump_output",
    steps: [
      `V_boost = ${V} bbl,  Pump Output = ${PO} bbl/stk`,
      `Strokes = ${V} / ${PO}`,
      `Strokes = ${round(n, 2)}`,
    ],
    inputs: { "Boost Line Volume (bbl)": V, "Pump Output (bbl/stk)": PO },
  };
}

// ---------- 42) Boost Line Circulation Time ----------
//   t (min) = V_boost / Q
export function boostLineCirculationTimeSrf({ boostLineVolume, pumpRate }) {
  const V = positive(num(boostLineVolume, "Boost Line Volume"), "Boost Line Volume");
  const Q = positive(num(pumpRate, "Pump Rate"), "Pump Rate");
  const t = V / Q;
  return {
    result: round(t, 2),
    unit: "min",
    formula: "t (min) = V_boost / Q",
    steps: [
      `V_boost = ${V} bbl,  Q = ${Q} bbl/min`,
      `t = ${V} / ${Q}`,
      `t = ${round(t, 2)} min`,
    ],
    inputs: { "Boost Line Volume (bbl)": V, "Pump Rate (bbl/min)": Q },
    extras: { "Time (hr)": round(t / 60, 3) },
  };
}

// ---------- 43) Auxiliary Line Capacity Per Foot ----------
//   C (bbl/ft) = ID² / 1029.4
export function auxiliaryLineCapacityPerFootSrf({ auxLineId }) {
  const ID = positive(num(auxLineId, "Auxiliary Line ID"), "Auxiliary Line ID");
  const v = (ID * ID) / 1029.4;
  return {
    result: round(v, 5),
    unit: "bbl/ft",
    formula: "C_aux (bbl/ft) = ID² / 1029.4",
    steps: [
      `Auxiliary Line ID = ${ID} in`,
      `C = ${ID}² / 1029.4`,
      `C = ${round(v, 5)} bbl/ft`,
    ],
    inputs: { "Auxiliary Line ID (in)": ID },
    extras: { "Capacity (gal/ft)": round(v * 42, 4) },
  };
}

// ---------- 44) Auxiliary Line Total Volume ----------
//   V (bbl) = ID² × L / 1029.4
export function auxiliaryLineTotalVolumeSrf({ auxLineId, auxLineLength }) {
  const ID = positive(num(auxLineId, "Auxiliary Line ID"), "Auxiliary Line ID");
  const L  = positive(num(auxLineLength, "Auxiliary Line Length"), "Auxiliary Line Length");
  const v = (ID * ID * L) / 1029.4;
  return {
    result: round(v, 2),
    unit: "bbl",
    formula: "V_aux (bbl) = ID² × L / 1029.4",
    steps: [
      `Auxiliary Line ID = ${ID} in,  Length = ${L} ft`,
      `V = ${ID}² × ${L} / 1029.4`,
      `V = ${round(v, 2)} bbl`,
    ],
    inputs: { "Auxiliary Line ID (in)": ID, "Auxiliary Line Length (ft)": L },
    extras: { "Volume (gal)": round(v * 42, 1) },
  };
}

// ---------- 45) Auxiliary Line Strokes ----------
//   Strokes = V_aux / pump_output
export function auxiliaryLineStrokesSrf({ auxLineVolume, pumpOutput }) {
  const V  = positive(num(auxLineVolume, "Auxiliary Line Volume"), "Auxiliary Line Volume");
  const PO = positive(num(pumpOutput, "Pump Output"), "Pump Output");
  const n = V / PO;
  return {
    result: round(n, 2),
    unit: "strokes",
    formula: "Strokes = V_aux / pump_output",
    steps: [
      `V_aux = ${V} bbl,  Pump Output = ${PO} bbl/stk`,
      `Strokes = ${V} / ${PO}`,
      `Strokes = ${round(n, 2)}`,
    ],
    inputs: { "Auxiliary Line Volume (bbl)": V, "Pump Output (bbl/stk)": PO },
  };
}
